import CoachingConstants from 'coaching/CoachingConstants';

type Requester = {
  name: string;
  email: string;
};

type Comment = {
  body: string;
};

type CustomField = {
  id: string;
  value: string;
};

type Request = {
  requester: Requester;
  subject: string;
  comment: Comment;
  ticket_form_id: string;
  custom_fields: CustomField[];
};

type RequestBody = {
  request: Request;
};

type ZendeskRequestFields = {
  requestReason: string;
  requestReasonLabel: string;
  email: string;
  studentFullName: string;
  details: string;
  name: string;
  userUuid?: string;
};

type CoachZendeskRequestFields = {
  requestReason: string;
  requestReasonLabel: string;
  email: string;
  details: string;
  name: string;
};

type RequestStatus = {
  success: boolean;
};

const REQUESTS_URL = `${CoachingConstants.R101_ZENDESK_COACHING_URL}/api/v2/requests.json`;

export function postZendeskRequest(fields: ZendeskRequestFields): Promise<RequestStatus> {
  return fetch(REQUESTS_URL, formFieldsToFetchOptions(fields))
    .then(res => {
      if (res.ok) {
        return { success: true };
      }

      return logZendeskError(fields);
    })
    .catch(() => logZendeskError(fields));
}

export function postCoachZendeskRequest(fields: CoachZendeskRequestFields): Promise<RequestStatus> {
  return fetch(REQUESTS_URL, coachFormFieldsToFetchOptions(fields))
    .then(res => {
      if (res.ok) {
        return { success: true };
      }

      return logZendeskError(fields);
    })
    .catch(() => logZendeskError(fields));
}

function coachFormFieldsToFetchOptions(fields: CoachZendeskRequestFields): RequestInit {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: emailToBasicAuthHeader(fields.email),
    },
    body: coachFormFieldsToReqBody(fields),
  };
}

function formFieldsToFetchOptions(fields: ZendeskRequestFields): RequestInit {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: emailToBasicAuthHeader(fields.email),
    },
    body: formFieldsToReqBody(fields),
  };
}

function emailToBasicAuthHeader(userEmail: string): string {
  // https://developer.zendesk.com/rest_api/docs/support/requests
  const encoded = btoa(`${userEmail}/token:${CoachingConstants.ZENDESK_API_TOKEN}`);

  return `Basic ${encoded}`;
}

const COACHING_HELP_FORM_ID = '360001294399';
const CASE_TYPE_ID = '360017653580';
const EMAIL_ID = '360017763219';
const STUDENT_NAME_ID = '360017698879';
const USER_UUID_ID = '360018089759';
const YOUR_NAME_ID = '360017653560';

function formFieldsToReqBody(fields: ZendeskRequestFields): string {
  const {
    email,
    name,
    userUuid,
    details,
    requestReason,
    requestReasonLabel,
    studentFullName,
  } = fields;

  const body: RequestBody = {
    request: {
      requester: {
        email,
        name,
      },
      comment: {
        body: details,
      },
      ticket_form_id: COACHING_HELP_FORM_ID,
      custom_fields: [
        {
          id: CASE_TYPE_ID,
          value: requestReason,
        },
        {
          id: EMAIL_ID,
          value: email,
        },
        {
          id: STUDENT_NAME_ID,
          value: studentFullName,
        },
      ],
      subject: requestReasonLabel,
    },
  };

  if (name) {
    body.request.custom_fields.push({
      id: YOUR_NAME_ID,
      value: name,
    });
  }

  if (userUuid) {
    body.request.custom_fields.push({
      id: USER_UUID_ID,
      value: userUuid,
    });
  }

  return JSON.stringify(body);
}

const COACHING_COACH_HELP_FORM_ID = '360001340019';
const COACH_CASE_TYPE_ID = '360018480839';
const COACH_NAME_ID = '360041668971';
const COACH_EMAIL_ID = '360041655651';

function coachFormFieldsToReqBody(fields: CoachZendeskRequestFields): string {
  const { email, name, details, requestReason, requestReasonLabel } = fields;

  const body: RequestBody = {
    request: {
      requester: {
        email,
        name,
      },
      comment: {
        body: details,
      },
      ticket_form_id: COACHING_COACH_HELP_FORM_ID,
      custom_fields: [
        {
          id: COACH_CASE_TYPE_ID,
          value: requestReason,
        },
        {
          id: COACH_EMAIL_ID,
          value: email,
        },
        {
          id: COACH_NAME_ID,
          value: name,
        },
      ],
      subject: requestReasonLabel,
    },
  };

  return JSON.stringify(body);
}

function logZendeskError(fields: ZendeskRequestFields | CoachZendeskRequestFields): RequestStatus {
  console.error('zendesk - problem submitting form to Zendesk', { fields });
  return { success: false };
}
